import type { MemoBlock } from "@/lib/types";
import { buildMemoSections } from "@/lib/memoSections";

// A navigable unit in presentation mode: a Zwischenabschnitt (sub-heading + the
// points under it), or a whole Abschnitt when the block has no sub-headings, or
// the run of points before a block's first sub-heading. Derived in document
// order across the whole memo, so the presentation cursor is single-level.
export interface FocusUnit {
  blockId: string;
  blockIndex: number;
  blockTitle: string;
  /** The sub-heading entry id, or null for a block-level / leading unit. */
  subheadingEntryId: string | null;
  /** Sub-heading title, or null when this unit has no sub-heading. */
  subheadingTitle: string | null;
  /** Point entry ids in this unit (the sub-heading entry itself is excluded). */
  entryIds: string[];
  /** Unique anchor: the sub-heading entry id, else the block id. */
  anchorId: string;
}

/** The DOM id of the unit's container — the single element used for scroll
 * centring, dimming and pointer-events. */
/** A unit's vertical extent in viewport coordinates (a DOMRect slice). */
export interface UnitExtent {
  top: number;
  bottom: number;
}

/** An unmounted unit carries a sentinel extent, never a real box. */
const measurable = (e: UnitExtent): boolean => Number.isFinite(e.top) && Number.isFinite(e.bottom);

/**
 * Where the reader's eye is: the upper third of the viewport. Readers do not look
 * at the middle of a screen and they do not look at the top edge — they read the
 * line about a third of the way down, and scroll to keep it there.
 */
export const READING_LINE = 1 / 3;

/**
 * The reading line, in viewport coordinates — CLAMPED by how far the reader has
 * actually scrolled.
 *
 * 🔴 The clamp is what makes this correct at rest, and it is not optional. An
 * unclamped line sits at `viewportH / 3` — 300px on a windowed browser, but 450px
 * on a maximized 1440p screen. The memo header occupies the first ~234px of the
 * scroll container, so on a large display the line lands BELOW a short first
 * Zwischenabschnitt and the first ↓ of the presentation locks section TWO. That is
 * `ccc994b`, and it is invisible to any test that hardcodes one viewport height.
 *
 * With the clamp, at `scrollTop = 0` the line sits at the very top of the container,
 * so the first unit always wins — for any viewport, any header. It only descends to
 * the reading line as the reader scrolls past it.
 *
 * This is NOT rejected rule #2 ("use scrollTop"): that one used the scroll position
 * as a SIGN TEST for "have I read past unit 0", which the header height corrupted.
 * Here it is a clamp on a probe, it is what real scroll-spy does, and the probe's
 * position in DOCUMENT space (`scrollTop + line`) stays strictly increasing — so
 * monotonicity survives.
 */
export function readingLine(viewTop: number, viewBottom: number, scrollTop: number): number {
  const ideal = (viewBottom - viewTop) * READING_LINE;
  return viewTop + Math.min(ideal, Math.max(0, scrollTop));
}

/**
 * The unit the reader is currently on — used to GRAB a unit when the first cursor
 * key arrives after free scrolling (the key does not advance; it locks what you are
 * already reading).
 *
 *   The FIRST unit whose bottom edge is past the READING LINE.
 *
 * This is scroll-spy — what a docs sidebar uses to decide which heading is
 * "current". It is not an invention, and that is the point: FIVE hand-rolled rules
 * failed here before it.
 *
 * Why it is right where they were wrong:
 *   - **Monotone by construction.** The probe's position in DOCUMENT space
 *     (`scrollTop + line`) is strictly increasing, and "first unit whose bottom is
 *     past it" is non-decreasing in the probe. It cannot jump backwards; no cliff.
 *   - **Short units are grabbable.** A 2-4 bullet Zwischenabschnitt is 150-250px on a
 *     ~900px screen. Any rule scored on VISIBLE AREA can only ever pick a unit taller
 *     than a THIRD of the screen, so the typical Zwischenabschnitt was structurally
 *     unreachable: the grab locked its NEIGHBOUR, and `Enter` filed the entry into
 *     the wrong section on a screen-shared protocol.
 *   - **A gap resolves forward.** Between two Abschnitte sits the next block's TITLE —
 *     ~58px with no unit in it. "First bottom past the line" hands the cursor to the
 *     unit BELOW the gap, which is the one whose heading the reader is looking at.
 *   - **No special case at the top**, and none needed: the clamped line starts at the
 *     container's top edge, so the first unit always wins at rest. Every previous
 *     attempt needed a "still at the beginning" branch, and it was that branch, every
 *     time, that masked the real bug.
 *
 * 🔴 Note how close this is to the rule that was here ORIGINALLY — "the first unit
 * that has not scrolled past the top edge" (`bottom > viewTop + 8`). Its shape was
 * right all along. It simply probed at the TOP EDGE, where a 20px sliver of the
 * departing section still qualifies and wins — the reported bug. The whole defect
 * was the probe's position, and five rewrites went looking for it somewhere else.
 *
 * The five rejected rules, so nobody re-derives them:
 *   1. `bottom > viewTop + epsilon` — probes at the top edge; a departing sliver wins.
 *   2. "Earliest ENTIRELY-visible unit." Rested on "a departing section is cut off at
 *      the top edge" — true only while it is TALLER than the distance you scrolled
 *      past it, which a short Zwischenabschnitt is not.
 *   3. "`scrollTop` as a sign test for 'have I read past unit 0'." The memo header
 *      lives INSIDE the scroll container, so ~234px of scroll happen first.
 *   4. "`first.top >= viewTop`." A logical inversion: "read past => above the fold" is
 *      true, its converse is not. Flipped the grab on ONE pixel of scroll.
 *   5. "Largest visible area (+ a first-unit branch)." Cannot see short units at all.
 */
export function pickNearestUnit(
  extents: UnitExtent[],
  viewTop: number,
  viewBottom: number,
  scrollTop: number,
): number {
  if (extents.length === 0) return 0;
  const firstMounted = extents.findIndex(measurable);
  // Nothing measurable (an initial mount frame where no unit has a DOM node):
  // fall back to the first unit rather than the last, which is what "scrolled past
  // everything" means and would be a surprising place to start.
  if (firstMounted === -1) return 0;

  const line = readingLine(viewTop, viewBottom, scrollTop);

  // The first unit not yet entirely above the reading line.
  for (let i = 0; i < extents.length; i++) {
    const e = extents[i];
    if (e && measurable(e) && e.bottom > line) return i;
  }
  // Scrolled past every unit (into the trailing whitespace): stay on the last MOUNTED
  // one — answering `extents.length - 1` blindly could hand back a unit with no DOM
  // node, and the caller would then dim the whole memo with nothing highlighted.
  for (let i = extents.length - 1; i >= 0; i--) {
    const e = extents[i];
    if (e && measurable(e)) return i;
  }
  return firstMounted;
}

/**
 * The `after_entry_id` for an entry inserted into `unit` from the keyboard while
 * presenting — the unit's LAST point, else its sub-heading, else nothing (an
 * empty block-level unit → append at the block's end).
 *
 * The backend does the rest: `services/memos.py::resolve_insert_anchor` clamps an
 * insert whose anchor is old-dated (which a carried-forward point almost always
 * is) to the END of the anchor's Zwischenabschnitt, ahead of the next sub-heading.
 * A sub-heading anchor carries no `log_date` and takes the same path, landing the
 * entry directly under the heading. A point typed earlier TODAY is already in
 * today's date group, so the entry lands right after it and a run of bullets keeps
 * typing order. All three are what "add to the section I am looking at" means — no
 * backend change is needed.
 */
export function insertAnchorFor(unit: FocusUnit): string | undefined {
  return unit.entryIds.at(-1) ?? unit.subheadingEntryId ?? undefined;
}

export function unitDomId(unit: { anchorId: string }): string {
  return `memo-unit-${unit.anchorId}`;
}

/**
 * The presentation cursor's units, derived from the SAME walk the screen renders
 * (`buildMemoSections`). They used to be two independent positional walks that had
 * to agree but could not be checked against each other; the render walk kept
 * updates, this one dropped them, and nothing pinned the pair together.
 */
export function buildFocusUnits(blocks: MemoBlock[]): FocusUnit[] {
  return blocks.flatMap((block, blockIndex) =>
    buildMemoSections(block).map((s) => ({
      blockId: block.id,
      blockIndex,
      blockTitle: block.title,
      subheadingEntryId: s.heading?.id ?? null,
      subheadingTitle: s.heading?.body ?? null,
      // An update reports on a to-do — it is content, not a navigable point, and
      // landing the presentation spotlight on it would be noise.
      entryIds: s.entries.filter((e) => e.kind !== "update").map((e) => e.id),
      anchorId: s.anchorId,
    })),
  );
}
